import React, { useState, useEffect, useContext } from 'react';
import {
    Save,
    Upload,
    Download,
    Eye,
    Copy,
    Palette,
    FileText,
    Settings,
    CreditCard,
    Mail,
    Globe,
    Calculator,
    Zap,
    Shield,
    Smartphone,
    Printer,
    QrCode,
    Banknote,
    Clock,
    Users,
    Building,
    MapPin,
    Phone,
    Star,
    Camera,
    Type,
    Layout,
    Hash,
    Calendar,
    DollarSign,
    Percent,
    AlertCircle,
    Check,
    X,
    Plus,
    Trash2,
    Edit3,
    Image,
    FileImage,
    Brush,
    RotateCcw,
    ChevronDown,
    ChevronUp,
    Info,
    Bell,
    MessageCircle,
    Archive,
    Lock
} from 'lucide-react';
import styles from './InvoiceSettings.module.css';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import { toast } from 'react-toastify';
import { FaToggleOff, FaToggleOn } from 'react-icons/fa6';

const InvoiceSettings = () => {
    const authCtx = useContext(AuthContext)
    const loginData = JSON.parse(authCtx?.loginData)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = loginData?.partnersData?.vendorIdentifier

    const [activeTab, setActiveTab] = useState('branding');
    const [previewMode, setPreviewMode] = useState(false);
    const [settings, setSettings] = useState({
        // Branding & Design
        branding: {
            logo: null,
            logoPosition: 'top-left',
            logoSize: 'medium',
            companyName: 'Lokaci Pro Store',
            tagline: 'Quality Products & Services',
            primaryColor: '#6366f1',
            secondaryColor: '#f3f4f6',
            accentColor: '#10b981',
            fontFamily: 'Inter',
            fontSize: 'medium',
            theme: 'modern',
            watermark: false,
            background: 'white',
            customCSS: ''
        },

        // Company Information
        company: {
            name: 'Lokaci Pro Store',
            address: '123 Business Street',
            city: 'Ahmedabad',
            state: 'Gujarat',
            zipCode: '380001',
            country: 'India',
            phone: '+91 9876543210',
            email: 'contact@lokacistore.com',
            website: 'www.lokacistore.com',
            taxId: 'GST123456789',
            registrationNumber: 'REG123456',
            bankDetails: {
                name: 'State Bank of India',
                accountNumber: '1234567890',
                routingNumber: 'SBIN0001234',
                ifsc: 'SBIN0001234'
            }
        },

        // Invoice Configuration
        invoice: {
            prefix: 'INV',
            startingNumber: 1001,
            numberFormat: '{prefix}-{number}',
            dateFormat: 'DD/MM/YYYY',
            currency: 'INR',
            currencySymbol: '₹',
            currencyPosition: 'before',
            decimalPlaces: 2,
            showItemCodes: true,
            showItemImages: false,
            showDiscounts: true,
            showTaxes: true,
            showShipping: true,
            itemColumns: ['description', 'quantity', 'rate', 'amount'],
            footerText: 'Thank you for your business!',
            termsAndConditions: 'Payment due within 30 days.',
            notes: '',
            dueDate: 30, // days
            language: 'english',
            timezone: 'Asia/Kolkata',
            // isGstBill: true
        },

        // Payment Settings
        payment: {
            methods: ['cash', 'card', 'bank_transfer', 'upi', 'cheque'],
            showQRCode: true,
            upiId: 'store@paytm',
            bankQRCode: true,
            paymentLinks: true,
            onlinePayment: true,
            partialPayments: true,
            installments: false,
            lateFees: {
                enabled: false,
                percentage: 2,
                afterDays: 30
            },
            discounts: {
                earlyPayment: 2,
                bulkOrder: 5,
                loyalty: 3
            }
        },

        // Automation & Workflow
        automation: {
            autoSend: false,
            sendReminders: true,
            reminderDays: [7, 3, 1],
            autoArchive: true,
            archiveAfterDays: 90,
            autoBackup: true,
            backupFrequency: 'weekly',
            notifications: {
                email: true,
                sms: false,
                whatsapp: true,
                push: true
            },
            followUp: {
                enabled: true,
                firstReminder: 7,
                secondReminder: 14,
                finalNotice: 30
            }
        },

        // Templates & Layout
        templates: {
            selectedTemplate: 'modern',
            customTemplates: [],
            layout: 'standard',
            headerStyle: 'full-width',
            tableStyle: 'bordered',
            footerStyle: 'compact',
            margins: 'normal',
            pageSize: 'A4',
            orientation: 'portrait'
        },

        // Security & Compliance
        security: {
            passwordProtected: false,
            digitalSignature: false,
            encryption: true,
            auditTrail: true,
            dataRetention: 365, // days
            gdprCompliant: true,
            accessControl: 'admin-only',
            ipWhitelist: [],
            twoFactorAuth: false
        }
    });

    const [expandedSections, setExpandedSections] = useState({});

    const tabs = [
        { id: 'branding', label: 'Branding & Design', icon: Palette },
        { id: 'company', label: 'Company Info', icon: Building },
        { id: 'invoice', label: 'Invoice Config', icon: FileText },
        { id: 'payment', label: 'Payment Settings', icon: CreditCard },
        { id: 'automation', label: 'Automation', icon: Zap },
        { id: 'templates', label: 'Templates', icon: Layout },
        { id: 'security', label: 'Security', icon: Shield }
    ];

    const themes = [
        { id: 'modern', name: 'Modern', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { id: 'classic', name: 'Classic', preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        { id: 'minimal', name: 'Minimal', preview: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { id: 'corporate', name: 'Corporate', preview: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
        { id: 'creative', name: 'Creative', preview: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }
    ];

    const currencies = [
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'JPY', symbol: '¥', name: 'Japanese Yen' }
    ];

    const updateSetting = (category, key, value) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }));
    };

    const updateNestedSetting = (category, parentKey, key, value) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [parentKey]: {
                    ...prev[category][parentKey],
                    [key]: value
                }
            }
        }));
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleFileUpload = (category, key, event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                updateSetting(category, key, e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const saveSettings = () => {
        // Save to local state (in real app, would save to backend)
        localStorage.setItem('lokaciInvoiceSettings', JSON.stringify(settings));
        console.log(settings)
        alert('Settings saved successfully!');
    };

    const resetToDefaults = () => {
        if (window.confirm('Are you sure you want to reset all settings to default? This cannot be undone.')) {
            localStorage.removeItem('lokaciInvoiceSettings');
            window.location.reload();
        }
    };

    const exportSettings = () => {
        const dataStr = JSON.stringify(settings, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const exportFileDefaultName = 'lokaci-invoice-settings.json';
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const getStoreInvoiceSettings = async () => {
        const response = await fetch(BASE_URL_API + "getStoreInvoiceSettings",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {

            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                }
            }
            console.log(respo)
        }
    }

    // 36ACDPE8918L1ZB

    const putStoreGST = async () => {
        const response = await fetch(BASE_URL_API + "putStoreGST",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    legalName: activeStore?.storeName,
                    tradeName: activeStore?.storeName,
                    constitutionOfBusiness: activeStore?.storeAddress,
                    businessAddress: activeStore?.storeAddress,
                    gstNumber: settings?.company?.registrationNumber
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                toast("Successfully updated gst number!")
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    toast(respo?.message)
                }
            }
            console.log(respo)
        }
    }

    // Load settings on component mount
    useEffect(() => {
        if (activeStore !== null) {
            getStoreInvoiceSettings()
        }
        // const saved = localStorage.getItem('lokaciInvoiceSettings');
        // if (saved) {
        //     setSettings(JSON.parse(saved));
        // }
    }, [activeStore]);

    const invoiceSettingsHandler = (category, key, value) => {
        console.log("invoice", "key", "value");
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }));
    }

    const renderBrandingTab = () => (
        <div className={styles.tabContent}>
            {/* Logo Upload */}
            <div className={`${styles.section} ${styles.brandingSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Camera className={styles.sectionIcon} size={20} />
                    Company Logo
                </h3>
                <div className={styles.gridTwoCol}>
                    <div>
                        <div className={styles.logoUpload}>
                            {settings.branding.logo ? (
                                <div className={styles.logoPreview}>
                                    <img src={settings.branding.logo} alt="Logo" className={styles.logoImage} />
                                    <button
                                        onClick={() => updateSetting('branding', 'logo', null)}
                                        className={styles.removeButton}
                                    >
                                        <Trash2 size={16} className={styles.buttonIcon} />
                                        Remove Logo
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.uploadPlaceholder}>
                                    <Upload size={32} className={styles.uploadIcon} />
                                    <div className={styles.uploadText}>
                                        <p>Upload your company logo</p>
                                        <p className={styles.uploadSubtext}>PNG, JPG up to 2MB</p>
                                    </div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileUpload('branding', 'logo', e)}
                                        className={styles.hiddenInput}
                                        id="logo-upload"
                                    />
                                    <label htmlFor="logo-upload" className={styles.uploadButton}>
                                        <Upload size={16} className={styles.buttonIcon} />
                                        Choose File
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.logoControls}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Logo Position</label>
                            <select
                                value={settings.branding.logoPosition}
                                onChange={(e) => updateSetting('branding', 'logoPosition', e.target.value)}
                                className={styles.select}
                            >
                                <option value="top-left">Top Left</option>
                                <option value="top-center">Top Center</option>
                                <option value="top-right">Top Right</option>
                                <option value="center">Center</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Logo Size</label>
                            <select
                                value={settings.branding.logoSize}
                                onChange={(e) => updateSetting('branding', 'logoSize', e.target.value)}
                                className={styles.select}
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                                <option value="extra-large">Extra Large</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Color Scheme */}
            <div className={`${styles.section} ${styles.colorSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Palette className={styles.sectionIcon} size={20} />
                    Color Scheme
                </h3>
                <div className={styles.gridThreeCol}>
                    {[
                        { key: 'primaryColor', label: 'Primary Color' },
                        { key: 'secondaryColor', label: 'Secondary Color' },
                        { key: 'accentColor', label: 'Accent Color' }
                    ].map(color => (
                        <div key={color.key} className={styles.formGroup}>
                            <label className={styles.label}>{color.label}</label>
                            <div className={styles.colorInput}>
                                <input
                                    type="color"
                                    value={settings.branding[color.key]}
                                    onChange={(e) => updateSetting('branding', color.key, e.target.value)}
                                    className={styles.colorPicker}
                                />
                                <input
                                    type="text"
                                    value={settings.branding[color.key]}
                                    onChange={(e) => updateSetting('branding', color.key, e.target.value)}
                                    className={styles.colorText}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Theme Selection */}
            <div className={`${styles.section} ${styles.themeSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Brush className={styles.sectionIcon} size={20} />
                    Theme Selection
                </h3>
                <div className={styles.themeGrid}>
                    {themes.map(theme => (
                        <div
                            key={theme.id}
                            onClick={() => updateSetting('branding', 'theme', theme.id)}
                            className={`${styles.themeCard} ${settings.branding.theme === theme.id ? styles.themeCardActive : ''}`}
                        >
                            <div
                                className={styles.themePreview}
                                style={{ background: theme.preview }}
                            ></div>
                            <p className={styles.themeName}>{theme.name}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Typography */}
            <div className={`${styles.section} ${styles.typographySection}`}>
                <h3 className={styles.sectionTitle}>
                    <Type className={styles.sectionIcon} size={20} />
                    Typography
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Font Family</label>
                        <select
                            value={settings.branding.fontFamily}
                            onChange={(e) => updateSetting('branding', 'fontFamily', e.target.value)}
                            className={styles.select}
                        >
                            <option value="Inter">Inter</option>
                            <option value="Roboto">Roboto</option>
                            <option value="Open Sans">Open Sans</option>
                            <option value="Lato">Lato</option>
                            <option value="Montserrat">Montserrat</option>
                            <option value="Poppins">Poppins</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Font Size</label>
                        <select
                            value={settings.branding.fontSize}
                            onChange={(e) => updateSetting('branding', 'fontSize', e.target.value)}
                            className={styles.select}
                        >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCompanyTab = () => (
        <div className={styles.tabContent}>
            {/* Basic Information */}
            <div className={`${styles.section} ${styles.companySection}`}>
                <h3 className={styles.sectionTitle}>
                    <Building className={styles.sectionIcon} size={20} />
                    Company Details
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Company Name</label>
                        <input
                            type="text"
                            value={settings.company.name}
                            onChange={(e) => updateSetting('company', 'name', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Email</label>
                        <input
                            type="email"
                            value={settings.company.email}
                            onChange={(e) => updateSetting('company', 'email', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Phone</label>
                        <input
                            type="tel"
                            value={settings.company.phone}
                            onChange={(e) => updateSetting('company', 'phone', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Website</label>
                        <input
                            type="url"
                            value={settings.company.website}
                            onChange={(e) => updateSetting('company', 'website', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                </div>
            </div>

            {/* Address Information */}
            <div className={`${styles.section} ${styles.addressSection}`}>
                <h3 className={styles.sectionTitle}>
                    <MapPin className={styles.sectionIcon} size={20} />
                    Address Information
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label className={styles.label}>Street Address</label>
                        <input
                            type="text"
                            value={settings.company.address}
                            onChange={(e) => updateSetting('company', 'address', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>City</label>
                        <input
                            type="text"
                            value={settings.company.city}
                            onChange={(e) => updateSetting('company', 'city', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>State</label>
                        <input
                            type="text"
                            value={settings.company.state}
                            onChange={(e) => updateSetting('company', 'state', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>ZIP Code</label>
                        <input
                            type="text"
                            value={settings.company.zipCode}
                            onChange={(e) => updateSetting('company', 'zipCode', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Country</label>
                        <input
                            type="text"
                            value={settings.company.country}
                            onChange={(e) => updateSetting('company', 'country', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                </div>
            </div>

            {/* Legal & Tax Information */}
            <div className={`${styles.section} ${styles.legalSection}`}>
                <h3 className={styles.sectionTitle}>
                    <div>
                        <FileText className={styles.sectionIcon} size={20} />
                        Legal & Tax Information
                    </div>
                    <div onClick={() => invoiceSettingsHandler('invoice', 'isGstBill', !settings?.invoice?.isGstBill)} className={styles.isGstBill} >
                        Is GST Bill?
                        {settings?.invoice?.isGstBill === true ? <FaToggleOn size={28} color="#4F46E5" /> : <FaToggleOff size={28} />}
                    </div>
                </h3>
                <div className={styles.gridTwoCol}>
                    {/* <div className={styles.formGroup}>
                        <label className={styles.label}>Tax ID (GST)</label>
                        <input
                            type="text"
                            value={settings.company.taxId}
                            onChange={(e) => updateSetting('company', 'taxId', e.target.value)}
                            className={styles.input}
                        />
                    </div> */}
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Registration Number</label>
                        <input
                            type="text"
                            value={settings?.company?.registrationNumber}
                            onChange={(e) => updateSetting('company', 'registrationNumber', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}></label>
                        <button className={styles.saveGst} onClick={() => putStoreGST()} >Save</button>
                    </div>
                </div>
            </div>

            {/* Bank Details */}
            <div className={`${styles.section} ${styles.bankSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Banknote className={styles.sectionIcon} size={20} />
                    Bank Details
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Bank Name</label>
                        <input
                            type="text"
                            value={settings.company.bankDetails.name}
                            onChange={(e) => updateNestedSetting('company', 'bankDetails', 'name', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Account Number</label>
                        <input
                            type="text"
                            value={settings.company.bankDetails.accountNumber}
                            onChange={(e) => updateNestedSetting('company', 'bankDetails', 'accountNumber', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>IFSC Code</label>
                        <input
                            type="text"
                            value={settings.company.bankDetails.ifsc}
                            onChange={(e) => updateNestedSetting('company', 'bankDetails', 'ifsc', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Routing Number</label>
                        <input
                            type="text"
                            value={settings.company.bankDetails.routingNumber}
                            onChange={(e) => updateNestedSetting('company', 'bankDetails', 'routingNumber', e.target.value)}
                            className={styles.input}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderInvoiceTab = () => (
        <div className={styles.tabContent}>
            {/* Invoice Numbering */}
            <div className={`${styles.section} ${styles.numberingSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Hash className={styles.sectionIcon} size={20} />
                    Invoice Numbering
                </h3>
                <div className={styles.gridThreeCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Invoice Prefix</label>
                        <input
                            type="text"
                            value={settings.invoice.prefix}
                            onChange={(e) => updateSetting('invoice', 'prefix', e.target.value)}
                            className={styles.input}
                            placeholder="INV"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Starting Number</label>
                        <input
                            type="number"
                            value={settings.invoice.startingNumber}
                            onChange={(e) => updateSetting('invoice', 'startingNumber', parseInt(e.target.value))}
                            className={styles.input}
                            min="1"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Number Format</label>
                        <select
                            value={settings.invoice.numberFormat}
                            onChange={(e) => updateSetting('invoice', 'numberFormat', e.target.value)}
                            className={styles.select}
                        >
                            <option value="{prefix}-{number}">{settings.invoice.prefix}-{settings.invoice.startingNumber}</option>
                            <option value="{prefix}{number}">{settings.invoice.prefix}{settings.invoice.startingNumber}</option>
                            <option value="{number}-{prefix}">{settings.invoice.startingNumber}-{settings.invoice.prefix}</option>
                            <option value="{prefix}-{date}-{number}">{settings.invoice.prefix}-{new Date().getFullYear()}-{settings.invoice.startingNumber}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Currency & Formatting */}
            <div className={`${styles.section} ${styles.currencySection}`}>
                <h3 className={styles.sectionTitle}>
                    <DollarSign className={styles.sectionIcon} size={20} />
                    Currency & Formatting
                </h3>
                <div className={styles.gridFourCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Currency</label>
                        <select
                            value={settings.invoice.currency}
                            onChange={(e) => {
                                const selectedCurrency = currencies.find(c => c.code === e.target.value);
                                updateSetting('invoice', 'currency', e.target.value);
                                updateSetting('invoice', 'currencySymbol', selectedCurrency.symbol);
                            }}
                            className={styles.select}
                        >
                            {currencies.map(currency => (
                                <option key={currency.code} value={currency.code}>
                                    {currency.symbol} {currency.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Symbol Position</label>
                        <select
                            value={settings.invoice.currencyPosition}
                            onChange={(e) => updateSetting('invoice', 'currencyPosition', e.target.value)}
                            className={styles.select}
                        >
                            <option value="before">Before Amount</option>
                            <option value="after">After Amount</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Decimal Places</label>
                        <select
                            value={settings.invoice.decimalPlaces}
                            onChange={(e) => updateSetting('invoice', 'decimalPlaces', parseInt(e.target.value))}
                            className={styles.select}
                        >
                            <option value="0">0</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Date Format</label>
                        <select
                            value={settings.invoice.dateFormat}
                            onChange={(e) => updateSetting('invoice', 'dateFormat', e.target.value)}
                            className={styles.select}
                        >
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Invoice Elements */}
            <div className={`${styles.section} ${styles.elementsSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Layout className={styles.sectionIcon} size={20} />
                    Invoice Elements
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <h4 className={styles.subTitle}>Show on Invoice</h4>
                        {[
                            { key: 'showItemCodes', label: 'Item Codes' },
                            { key: 'showItemImages', label: 'Item Images' },
                            { key: 'showDiscounts', label: 'Discounts' },
                            { key: 'showTaxes', label: 'Taxes' },
                            { key: 'showShipping', label: 'Shipping Charges' }
                        ].map(item => (
                            <label key={item.key} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.invoice[item.key]}
                                    onChange={(e) => updateSetting('invoice', item.key, e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <span className={styles.checkboxText}>{item.label}</span>
                            </label>
                        ))}
                    </div>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.label}>Item Columns</label>
                        {['description', 'quantity', 'rate', 'amount', 'discount', 'tax'].map(column => (
                            <label key={column} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.invoice.itemColumns.includes(column)}
                                    onChange={(e) => {
                                        const columns = e.target.checked
                                            ? [...settings.invoice.itemColumns, column]
                                            : settings.invoice.itemColumns.filter(c => c !== column);
                                        updateSetting('invoice', 'itemColumns', columns);
                                    }}
                                    className={styles.checkbox}
                                />
                                <span className={styles.checkboxText}>{column.charAt(0).toUpperCase() + column.slice(1)}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Terms & Notes */}
            <div className={`${styles.section} ${styles.termsSection}`}>
                <h3 className={styles.sectionTitle}>
                    <FileText className={styles.sectionIcon} size={20} />
                    Terms & Notes
                </h3>
                <div className={styles.termsGrid}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Footer Text</label>
                        <input
                            type="text"
                            value={settings.invoice.footerText}
                            onChange={(e) => updateSetting('invoice', 'footerText', e.target.value)}
                            className={styles.input}
                            placeholder="Thank you for your business!"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Terms & Conditions</label>
                        <textarea
                            value={settings.invoice.termsAndConditions}
                            onChange={(e) => updateSetting('invoice', 'termsAndConditions', e.target.value)}
                            rows={3}
                            className={styles.textarea}
                            placeholder="Enter terms and conditions..."
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Additional Notes</label>
                        <textarea
                            value={settings.invoice.notes}
                            onChange={(e) => updateSetting('invoice', 'notes', e.target.value)}
                            rows={3}
                            className={styles.textarea}
                            placeholder="Additional notes for invoice..."
                        />
                    </div>
                    <div className={styles.gridTwoCol}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Default Due Days</label>
                            <input
                                type="number"
                                value={settings.invoice.dueDate}
                                onChange={(e) => updateSetting('invoice', 'dueDate', parseInt(e.target.value))}
                                className={styles.input}
                                min="0"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Language</label>
                            <select
                                value={settings.invoice.language}
                                onChange={(e) => updateSetting('invoice', 'language', e.target.value)}
                                className={styles.select}
                            >
                                <option value="english">English</option>
                                <option value="hindi">Hindi</option>
                                <option value="gujarati">Gujarati</option>
                                <option value="spanish">Spanish</option>
                                <option value="french">French</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPaymentTab = () => (
        <div className={styles.tabContent}>
            {/* Payment Methods */}
            <div className={`${styles.section} ${styles.paymentSection}`}>
                <h3 className={styles.sectionTitle}>
                    <CreditCard className={styles.sectionIcon} size={20} />
                    Payment Methods
                </h3>
                <div className={styles.paymentMethodsGrid}>
                    {[
                        { key: 'cash', label: 'Cash', icon: Banknote },
                        { key: 'card', label: 'Card', icon: CreditCard },
                        { key: 'bank_transfer', label: 'Bank Transfer', icon: Building },
                        { key: 'upi', label: 'UPI', icon: Smartphone },
                        { key: 'cheque', label: 'Cheque', icon: FileText }
                    ].map(method => {
                        const Icon = method.icon;
                        return (
                            <label key={method.key} className={styles.paymentMethod}>
                                <input
                                    type="checkbox"
                                    checked={settings.payment.methods.includes(method.key)}
                                    onChange={(e) => {
                                        const methods = e.target.checked
                                            ? [...settings.payment.methods, method.key]
                                            : settings.payment.methods.filter(m => m !== method.key);
                                        updateSetting('payment', 'methods', methods);
                                    }}
                                    className={styles.paymentCheckbox}
                                />
                                <Icon size={24} className={styles.paymentIcon} />
                                <span className={styles.paymentLabel}>{method.label}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Digital Payments */}
            <div className={`${styles.section} ${styles.digitalSection}`}>
                <h3 className={styles.sectionTitle}>
                    <QrCode className={styles.sectionIcon} size={20} />
                    Digital Payments
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.showQRCode}
                                onChange={(e) => updateSetting('payment', 'showQRCode', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Show QR Code on Invoice</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.bankQRCode}
                                onChange={(e) => updateSetting('payment', 'bankQRCode', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Bank QR Code</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.paymentLinks}
                                onChange={(e) => updateSetting('payment', 'paymentLinks', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Payment Links</span>
                        </label>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>UPI ID</label>
                        <input
                            type="text"
                            value={settings.payment.upiId}
                            onChange={(e) => updateSetting('payment', 'upiId', e.target.value)}
                            className={styles.input}
                            placeholder="yourstore@paytm"
                        />
                    </div>
                </div>
            </div>

            {/* Payment Options */}
            <div className={`${styles.section} ${styles.optionsSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Settings className={styles.sectionIcon} size={20} />
                    Payment Options
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.onlinePayment}
                                onChange={(e) => updateSetting('payment', 'onlinePayment', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Online Payment Gateway</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.partialPayments}
                                onChange={(e) => updateSetting('payment', 'partialPayments', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Allow Partial Payments</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.installments}
                                onChange={(e) => updateSetting('payment', 'installments', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Installment Plans</span>
                        </label>
                    </div>
                    <div className={styles.discountGroup}>
                        <h4 className={styles.subTitle}>Discount Settings (%)</h4>
                        <div className={styles.discountSettings}>
                            {[
                                { key: 'earlyPayment', label: 'Early Payment' },
                                { key: 'bulkOrder', label: 'Bulk Order' },
                                { key: 'loyalty', label: 'Loyalty Discount' }
                            ].map(discount => (
                                <div key={discount.key} className={styles.discountRow}>
                                    <span className={styles.discountLabel}>{discount.label}</span>
                                    <input
                                        type="number"
                                        value={settings.payment.discounts[discount.key]}
                                        onChange={(e) => updateNestedSetting('payment', 'discounts', discount.key, parseFloat(e.target.value))}
                                        className={styles.discountInput}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Late Fees */}
            <div className={`${styles.section} ${styles.lateFeesSection}`}>
                <h3 className={styles.sectionTitle}>
                    <AlertCircle className={styles.sectionIcon} size={20} />
                    Late Fees & Penalties
                </h3>
                <div className={styles.gridThreeCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.payment.lateFees.enabled}
                                onChange={(e) => updateNestedSetting('payment', 'lateFees', 'enabled', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Enable Late Fees</span>
                        </label>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Late Fee (%)</label>
                        <input
                            type="number"
                            value={settings.payment.lateFees.percentage}
                            onChange={(e) => updateNestedSetting('payment', 'lateFees', 'percentage', parseFloat(e.target.value))}
                            className={styles.input}
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={!settings.payment.lateFees.enabled}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>After Days</label>
                        <input
                            type="number"
                            value={settings.payment.lateFees.afterDays}
                            onChange={(e) => updateNestedSetting('payment', 'lateFees', 'afterDays', parseInt(e.target.value))}
                            className={styles.input}
                            min="1"
                            disabled={!settings.payment.lateFees.enabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAutomationTab = () => (
        <div className={styles.tabContent}>
            {/* Auto-send & Reminders */}
            <div className={`${styles.section} ${styles.automationSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Mail className={styles.sectionIcon} size={20} />
                    Auto-send & Reminders
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.automation.autoSend}
                                onChange={(e) => updateSetting('automation', 'autoSend', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Auto-send Invoices</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.automation.sendReminders}
                                onChange={(e) => updateSetting('automation', 'sendReminders', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Send Payment Reminders</span>
                        </label>
                    </div>
                    <div className={styles.reminderGroup}>
                        <label className={styles.label}>Reminder Days (before due date)</label>
                        <div className={styles.reminderDays}>
                            {[7, 3, 1].map(day => (
                                <label key={day} className={styles.reminderDay}>
                                    <input
                                        type="checkbox"
                                        checked={settings.automation.reminderDays.includes(day)}
                                        onChange={(e) => {
                                            const days = e.target.checked
                                                ? [...settings.automation.reminderDays, day].sort((a, b) => b - a)
                                                : settings.automation.reminderDays.filter(d => d !== day);
                                            updateSetting('automation', 'reminderDays', days);
                                        }}
                                        className={styles.reminderCheckbox}
                                        disabled={!settings.automation.sendReminders}
                                    />
                                    <span className={styles.reminderLabel}>{day}d</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Follow-up Settings */}
            <div className={`${styles.section} ${styles.followUpSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Clock className={styles.sectionIcon} size={20} />
                    Follow-up Settings
                </h3>
                <div className={styles.gridFourCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.automation.followUp.enabled}
                                onChange={(e) => updateNestedSetting('automation', 'followUp', 'enabled', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Enable Follow-ups</span>
                        </label>
                    </div>
                    {[
                        { key: 'firstReminder', label: 'First Reminder (days)' },
                        { key: 'secondReminder', label: 'Second Reminder (days)' },
                        { key: 'finalNotice', label: 'Final Notice (days)' }
                    ].map(field => (
                        <div key={field.key} className={styles.formGroup}>
                            <label className={styles.label}>{field.label}</label>
                            <input
                                type="number"
                                value={settings.automation.followUp[field.key]}
                                onChange={(e) => updateNestedSetting('automation', 'followUp', field.key, parseInt(e.target.value))}
                                className={styles.input}
                                min="1"
                                disabled={!settings.automation.followUp.enabled}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Notifications */}
            <div className={`${styles.section} ${styles.notificationSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Bell className={styles.sectionIcon} size={20} />
                    Notification Channels
                </h3>
                <div className={styles.notificationGrid}>
                    {[
                        { key: 'email', label: 'Email', icon: Mail },
                        { key: 'sms', label: 'SMS', icon: Smartphone },
                        { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
                        { key: 'push', label: 'Push Notifications', icon: AlertCircle }
                    ].map(channel => {
                        const Icon = channel.icon;
                        return (
                            <label key={channel.key} className={styles.notificationChannel}>
                                <input
                                    type="checkbox"
                                    checked={settings.automation.notifications[channel.key]}
                                    onChange={(e) => updateNestedSetting('automation', 'notifications', channel.key, e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <Icon size={16} className={styles.channelIcon} />
                                <span className={styles.channelLabel}>{channel.label}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Auto Archive & Backup */}
            <div className={`${styles.section} ${styles.backupSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Archive className={styles.sectionIcon} size={20} />
                    Auto Archive & Backup
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.automation.autoArchive}
                                onChange={(e) => updateSetting('automation', 'autoArchive', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Auto Archive Paid Invoices</span>
                        </label>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Archive After (days)</label>
                            <input
                                type="number"
                                value={settings.automation.archiveAfterDays}
                                onChange={(e) => updateSetting('automation', 'archiveAfterDays', parseInt(e.target.value))}
                                className={styles.input}
                                min="1"
                                disabled={!settings.automation.autoArchive}
                            />
                        </div>
                    </div>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.automation.autoBackup}
                                onChange={(e) => updateSetting('automation', 'autoBackup', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Auto Backup</span>
                        </label>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Backup Frequency</label>
                            <select
                                value={settings.automation.backupFrequency}
                                onChange={(e) => updateSetting('automation', 'backupFrequency', e.target.value)}
                                className={styles.select}
                                disabled={!settings.automation.autoBackup}
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTemplatesTab = () => (
        <div className={styles.tabContent}>
            {/* Template Selection */}
            <div className={`${styles.section} ${styles.templateSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Layout className={styles.sectionIcon} size={20} />
                    Template Selection
                </h3>
                <div className={styles.templateGrid}>
                    {[
                        { id: 'modern', name: 'Modern', preview: 'Clean and contemporary design' },
                        { id: 'classic', name: 'Classic', preview: 'Traditional professional layout' },
                        { id: 'minimal', name: 'Minimal', preview: 'Simple and elegant' },
                        { id: 'corporate', name: 'Corporate', preview: 'Formal business style' },
                        { id: 'creative', name: 'Creative', preview: 'Artistic and unique' },
                        { id: 'custom', name: 'Custom', preview: 'Your own template' }
                    ].map(template => (
                        <div
                            key={template.id}
                            onClick={() => updateSetting('templates', 'selectedTemplate', template.id)}
                            className={`${styles.templateCard} ${settings.templates.selectedTemplate === template.id ? styles.templateCardActive : ''}`}
                        >
                            <div className={styles.templatePreview}>
                                <FileText size={32} className={styles.templateIcon} />
                            </div>
                            <h4 className={styles.templateName}>{template.name}</h4>
                            <p className={styles.templateDescription}>{template.preview}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Layout Settings */}
            <div className={`${styles.section} ${styles.layoutSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Settings className={styles.sectionIcon} size={20} />
                    Layout Settings
                </h3>
                <div className={styles.gridThreeCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Page Size</label>
                        <select
                            value={settings.templates.pageSize}
                            onChange={(e) => updateSetting('templates', 'pageSize', e.target.value)}
                            className={styles.select}
                        >
                            <option value="A4">A4</option>
                            <option value="A3">A3</option>
                            <option value="Letter">Letter</option>
                            <option value="Legal">Legal</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Orientation</label>
                        <select
                            value={settings.templates.orientation}
                            onChange={(e) => updateSetting('templates', 'orientation', e.target.value)}
                            className={styles.select}
                        >
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Margins</label>
                        <select
                            value={settings.templates.margins}
                            onChange={(e) => updateSetting('templates', 'margins', e.target.value)}
                            className={styles.select}
                        >
                            <option value="narrow">Narrow</option>
                            <option value="normal">Normal</option>
                            <option value="wide">Wide</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Style Customization */}
            <div className={`${styles.section} ${styles.styleSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Brush className={styles.sectionIcon} size={20} />
                    Style Customization
                </h3>
                <div className={styles.gridThreeCol}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Header Style</label>
                        <select
                            value={settings.templates.headerStyle}
                            onChange={(e) => updateSetting('templates', 'headerStyle', e.target.value)}
                            className={styles.select}
                        >
                            <option value="full-width">Full Width</option>
                            <option value="centered">Centered</option>
                            <option value="split">Split Layout</option>
                            <option value="compact">Compact</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Table Style</label>
                        <select
                            value={settings.templates.tableStyle}
                            onChange={(e) => updateSetting('templates', 'tableStyle', e.target.value)}
                            className={styles.select}
                        >
                            <option value="bordered">Bordered</option>
                            <option value="striped">Striped</option>
                            <option value="minimal">Minimal</option>
                            <option value="modern">Modern</option>
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Footer Style</label>
                        <select
                            value={settings.templates.footerStyle}
                            onChange={(e) => updateSetting('templates', 'footerStyle', e.target.value)}
                            className={styles.select}
                        >
                            <option value="compact">Compact</option>
                            <option value="detailed">Detailed</option>
                            <option value="signature">With Signature</option>
                            <option value="minimal">Minimal</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSecurityTab = () => (
        <div className={styles.tabContent}>
            {/* Access Control */}
            <div className={`${styles.section} ${styles.securitySection}`}>
                <h3 className={styles.sectionTitle}>
                    <Shield className={styles.sectionIcon} size={20} />
                    Access Control
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.passwordProtected}
                                onChange={(e) => updateSetting('security', 'passwordProtected', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Password Protected Invoices</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.twoFactorAuth}
                                onChange={(e) => updateSetting('security', 'twoFactorAuth', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Two-Factor Authentication</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.digitalSignature}
                                onChange={(e) => updateSetting('security', 'digitalSignature', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Digital Signature</span>
                        </label>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Access Level</label>
                        <select
                            value={settings.security.accessControl}
                            onChange={(e) => updateSetting('security', 'accessControl', e.target.value)}
                            className={styles.select}
                        >
                            <option value="admin-only">Admin Only</option>
                            <option value="manager-plus">Manager & Above</option>
                            <option value="all-staff">All Staff</option>
                            <option value="custom">Custom Permissions</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Data Protection */}
            <div className={`${styles.section} ${styles.dataSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Lock className={styles.sectionIcon} size={20} />
                    Data Protection
                </h3>
                <div className={styles.gridTwoCol}>
                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.encryption}
                                onChange={(e) => updateSetting('security', 'encryption', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Data Encryption</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.auditTrail}
                                onChange={(e) => updateSetting('security', 'auditTrail', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>Audit Trail</span>
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.security.gdprCompliant}
                                onChange={(e) => updateSetting('security', 'gdprCompliant', e.target.checked)}
                                className={styles.checkbox}
                            />
                            <span className={styles.checkboxText}>GDPR Compliant</span>
                        </label>
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Data Retention (days)</label>
                        <input
                            type="number"
                            value={settings.security.dataRetention}
                            onChange={(e) => updateSetting('security', 'dataRetention', parseInt(e.target.value))}
                            className={styles.input}
                            min="30"
                            max="3650"
                        />
                        <p className={styles.helpText}>Minimum 30 days, maximum 10 years</p>
                    </div>
                </div>
            </div>

            {/* IP Whitelist */}
            <div className={`${styles.section} ${styles.ipSection}`}>
                <h3 className={styles.sectionTitle}>
                    <Globe className={styles.sectionIcon} size={20} />
                    IP Whitelist
                </h3>
                <div className={styles.ipWhitelistGroup}>
                    <p className={styles.description}>Add IP addresses that are allowed to access invoice settings (one per line)</p>
                    <textarea
                        value={settings.security.ipWhitelist.join('\n')}
                        onChange={(e) => updateSetting('security', 'ipWhitelist', e.target.value.split('\n').filter(ip => ip.trim()))}
                        rows={4}
                        className={styles.ipTextarea}
                        placeholder="192.168.1.1&#10;10.0.0.1&#10;203.0.113.1"
                    />
                    <div className={styles.infoNote}>
                        <Info size={16} className={styles.infoIcon} />
                        <span className={styles.infoText}>Leave empty to allow access from any IP address</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerInfo}>
                        <h1 className={styles.title}>Invoice Settings</h1>
                        <p className={styles.subtitle}>Configure your Lokaci Pro invoice system</p>
                    </div>
                    <div className={styles.headerActions}>
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            className={styles.previewButton}
                        >
                            <Eye size={16} className={styles.buttonIcon} />
                            {previewMode ? 'Hide Preview' : 'Preview'}
                        </button>
                        <button
                            onClick={exportSettings}
                            className={styles.exportButton}
                        >
                            <Download size={16} className={styles.buttonIcon} />
                            Export
                        </button>
                        <button
                            onClick={saveSettings}
                            className={styles.saveButton}
                        >
                            <Save size={16} className={styles.buttonIcon} />
                            Save Settings
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.mainContent}>
                <div className={styles.layout}>
                    {/* Sidebar Navigation */}
                    <div className={styles.sidebar}>
                        <div className={styles.sidebarCard}>
                            <nav className={styles.navigation}>
                                {tabs.map(tab => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`${styles.navItem} ${activeTab === tab.id ? styles.navItemActive : ''}`}
                                        >
                                            <Icon size={16} className={styles.navIcon} />
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </nav>

                            {/* Quick Actions */}
                            <div className={styles.quickActions}>
                                <h3 className={styles.quickActionsTitle}>Quick Actions</h3>
                                <div className={styles.quickActionsList}>
                                    <button
                                        onClick={resetToDefaults}
                                        className={styles.resetButton}
                                    >
                                        <RotateCcw size={16} className={styles.buttonIcon} />
                                        Reset to Defaults
                                    </button>
                                    <button
                                        onClick={() => {
                                            const input = document.createElement('input');
                                            input.type = 'file';
                                            input.accept = '.json';
                                            input.onchange = (e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onload = (e) => {
                                                        try {
                                                            const importedSettings = JSON.parse(e.target.result);
                                                            setSettings(importedSettings);
                                                            alert('Settings imported successfully!');
                                                        } catch (error) {
                                                            alert('Error importing settings. Please check the file format.');
                                                        }
                                                    };
                                                    reader.readAsText(file);
                                                }
                                            };
                                            input.click();
                                        }}
                                        className={styles.importButton}
                                    >
                                        <Upload size={16} className={styles.buttonIcon} />
                                        Import Settings
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className={styles.content}>
                        <div className={styles.contentCard}>
                            {activeTab === 'branding' && renderBrandingTab()}
                            {activeTab === 'company' && renderCompanyTab()}
                            {activeTab === 'invoice' && renderInvoiceTab()}
                            {activeTab === 'payment' && renderPaymentTab()}
                            {activeTab === 'automation' && renderAutomationTab()}
                            {activeTab === 'templates' && renderTemplatesTab()}
                            {activeTab === 'security' && renderSecurityTab()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {previewMode && (
                <div className={styles.previewOverlay}>
                    <div className={styles.previewModal}>
                        <div className={styles.previewHeader}>
                            <h2 className={styles.previewTitle}>Invoice Preview</h2>
                            <button
                                onClick={() => setPreviewMode(false)}
                                className={styles.closeButton}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className={styles.previewContent}>
                            {/* Sample Invoice Preview */}
                            <div className={styles.invoice}>
                                <div className={styles.invoiceHeader}>
                                    <div className={styles.invoiceCompany}>
                                        {settings.branding.logo && (
                                            <img src={settings.branding.logo} alt="Logo" className={styles.invoiceLogo} />
                                        )}
                                        <h1 className={styles.invoiceCompanyName} style={{ color: settings.branding.primaryColor }}>
                                            {settings.company.name}
                                        </h1>
                                        <p className={styles.invoiceTagline}>{settings.branding.tagline}</p>
                                    </div>
                                    <div className={styles.invoiceDetails}>
                                        <h2 className={styles.invoiceTitle}>INVOICE</h2>
                                        <p className={styles.invoiceNumber}>{settings.invoice.prefix}-{settings.invoice.startingNumber}</p>
                                        <p className={styles.invoiceDate}>{new Date().toLocaleDateString()}</p>
                                    </div>
                                </div>

                                <div className={styles.invoiceParties}>
                                    <div className={styles.invoiceParty}>
                                        <h3 className={styles.partyTitle}>Bill To:</h3>
                                        <div className={styles.partyDetails}>
                                            <p>Sample Customer</p>
                                            <p>123 Customer Street</p>
                                            <p>Customer City, State 12345</p>
                                        </div>
                                    </div>
                                    <div className={styles.invoiceParty}>
                                        <h3 className={styles.partyTitle}>From:</h3>
                                        <div className={styles.partyDetails}>
                                            <p>{settings.company.name}</p>
                                            <p>{settings.company.address}</p>
                                            <p>{settings.company.city}, {settings.company.state} {settings.company.zipCode}</p>
                                            <p>{settings.company.email}</p>
                                            <p>{settings.company.phone}</p>
                                        </div>
                                    </div>
                                </div>

                                <table className={styles.invoiceTable}>
                                    <thead>
                                        <tr className={styles.tableHeader}>
                                            <th className={styles.tableHeaderCell}>Description</th>
                                            <th className={styles.tableHeaderCell}>Qty</th>
                                            <th className={styles.tableHeaderCell}>Rate</th>
                                            <th className={styles.tableHeaderCell}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className={styles.tableRow}>
                                            <td className={styles.tableCell}>Sample Product</td>
                                            <td className={styles.tableCell}>2</td>
                                            <td className={styles.tableCell}>{settings.invoice.currencySymbol}50.00</td>
                                            <td className={styles.tableCell}>{settings.invoice.currencySymbol}100.00</td>
                                        </tr>
                                        <tr className={styles.tableRow}>
                                            <td className={styles.tableCell}>Sample Service</td>
                                            <td className={styles.tableCell}>1</td>
                                            <td className={styles.tableCell}>{settings.invoice.currencySymbol}75.00</td>
                                            <td className={styles.tableCell}>{settings.invoice.currencySymbol}75.00</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className={styles.invoiceTotal}>
                                    <div className={styles.totalSection}>
                                        <div className={styles.totalRow}>
                                            <span className={styles.totalLabel}>Subtotal:</span>
                                            <span className={styles.totalValue}>{settings.invoice.currencySymbol}175.00</span>
                                        </div>
                                        <div className={styles.totalRow}>
                                            <span className={styles.totalLabel}>Tax (18%):</span>
                                            <span className={styles.totalValue}>{settings.invoice.currencySymbol}31.50</span>
                                        </div>
                                        <div className={`${styles.totalRow} ${styles.totalRowFinal}`}>
                                            <span className={styles.totalLabel}>Total:</span>
                                            <span className={styles.totalValue}>{settings.invoice.currencySymbol}206.50</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.invoiceFooter}>
                                    <p className={styles.footerText}>{settings.invoice.footerText}</p>
                                    {settings.invoice.termsAndConditions && (
                                        <div className={styles.termsSection}>
                                            <h4 className={styles.termsTitle}>Terms & Conditions:</h4>
                                            <p className={styles.termsText}>{settings.invoice.termsAndConditions}</p>
                                        </div>
                                    )}
                                    {settings.payment.showQRCode && (
                                        <div className={styles.qrSection}>
                                            <div className={styles.qrCode}>
                                                <QrCode size={32} className={styles.qrIcon} />
                                            </div>
                                            <div className={styles.qrDetails}>
                                                <p className={styles.qrText}>Scan to pay via UPI</p>
                                                <p className={styles.qrId}>{settings.payment.upiId}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceSettings;

